/**
 * «Retos»: a quién puedo retar.
 *
 * SON DOS BÚSQUEDAS EN UNA PANTALLA, y cuál se hace lo decide si hay algo
 * escrito en el buscador:
 *
 *  · SIN TEXTO — «quién anda cerca». Arranca en 10 km a la redonda y la barra
 *    los mueve. Diez es la distancia a la que alguien va a jugar un martes sin
 *    pensarlo; el que quiera manejar una hora la sube.
 *  · CON TEXTO — «dónde está fulano», en todo el país. Los kilómetros dejan de
 *    aplicar A PROPÓSITO: quien escribe un nombre está buscando A ESA PERSONA,
 *    y que no aparezca por estar a 40 km es una lista vacía que desde acá no
 *    hay forma de entender. La barra se apaga y lo dice, en vez de quedarse
 *    puesta fingiendo que manda.
 *
 * LOS FILTROS SON CASILLAS, Y TODAS SE PUEDEN APAGAR — la de distancia
 * incluida. Antes los diez kilómetros eran el piso de la pantalla: quien vive
 * donde no hay nadie a diez kilómetros, o quien todavía no puso su ubicación,
 * no tenía forma de ver a nadie. Ahora se destilda y se busca en todo el
 * país. Cada casilla que se prende ABRE su control debajo —la barra, el
 * desplegable de clubes— en vez de tenerlos todos puestos ocupando pantalla
 * sin filtrar nada.
 *
 * LA BÚSQUEDA SE HACE SOLA al cambiar cualquier cosa, sin botón de «buscar»:
 * mover la barra y después tener que apretar otra cosa es el paso que hace
 * que nadie mueva la barra. Cada cambio CANCELA la anterior — sin eso, la
 * respuesta de «30 km» puede llegar después de la de «10 km» y pisarla.
 *
 * QUIEN NO TIENE UBICACIÓN NO VE UNA LISTA VACÍA, ve el botón para ponerla —
 * y al lado, la otra salida: apagar la distancia. Una pantalla que dice «no
 * hay nadie» cuando en realidad falta un dato del perfil es la forma más
 * segura de que alguien crea que el producto no sirve.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CHALLENGE_DEFAULT_KM,
  CHALLENGE_MAX_KM,
  CHALLENGE_TEXT_MIN,
  type ChallengeKind,
  type ChallengePlayer,
  type Community,
  type PlayerProfile,
} from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { CaraACara } from '../components/CaraACara';
import { MyChallenges } from '../components/MyChallenges';
import { ReportFriendly } from '../components/ReportFriendly';
import { Paginador, usePaginado } from '../components/Paginado';
import { ActivitySection } from '../components/ActivitySection';
import { WallSection } from '../components/WallSection';
import { PersonCell } from '../components/PersonCell';
import { LocationBox } from '../components/LocationBox';
import { Select } from '../components/Select';
import { ApiError, api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { useAnotarVisita } from '../hooks/useAnotarVisita';

/**
 * LAS CUATRO PESTAÑAS, en el orden en que se ven.
 *
 * «Mis retos» va última y no primera: al abrir Retos, lo primero que hay que
 * ver es qué pasó en el club. Los retos propios se miran cuando llega el
 * aviso, y el aviso trae la dirección con la pestaña puesta.
 */
const VISTAS = ['activity', 'upcoming', 'search', 'mine'] as const;
type Vista = (typeof VISTAS)[number];

/** Lo que se espera después de la última tecla antes de preguntarle al servidor. */
const ESPERA_MS = 300;

/** El valor del desplegable que significa «de cualquier club». */
const CUALQUIER_CLUB = '';

export function ChallengesPage(): JSX.Element {
  // El globito del inicio baja al entrar acá. Ver `useAnotarVisita`.
  useAnotarVisita('challenges');
  const { t } = useTranslation();

  /** Cada casilla, prendida o apagada. Ver el bloque de filtros más abajo. */
  /**
   * TRES PESTAÑAS EN UN SOLO RENGLÓN, y no dos niveles de pestañas.
   *
   * «Lo que pasó», «lo que se viene» y «con quién puedo jugar» son la misma
   * pregunta mirada en tres tiempos, así que van al mismo nivel. Un Muro con
   * sus propias sub-pestañas adentro de una pestaña de Retos es dos filas de
   * botones arriba de todo, y a esa altura ya nadie sabe dónde está parado.
   *
   * ARRANCA EN EL MURO. Al abrir Retos, lo primero que hay que ver es qué
   * pasó en el club: buscar rival es algo que se hace a propósito, y para eso
   * se toca la tercera.
   */
  const [params, setParams] = useSearchParams();
  /*
   * LA PESTAÑA VIENE DE LA DIRECCIÓN, no de un `useState` a secas.
   *
   * El aviso de «te retaron» lleva a `/retos?v=mine`, y `linkPath` es una
   * dirección: sin esto, el aviso abriría en la actividad del club y la
   * persona tendría que buscar sola dónde está el reto que le avisaron. De
   * paso, recargar la página ya no devuelve a la primera pestaña.
   *
   * Lo que no se entiende cae en `activity`: una dirección con `?v=cualquier`
   * —compartida a mano, o de una versión vieja— tiene que abrir en algo.
   */
  const pedida = params.get('v');
  const vista: Vista = VISTAS.includes(pedida as Vista) ? (pedida as Vista) : 'activity';

  /*
    UN CONTADOR PARA VOLVER A PEDIR LA LISTA. Al anotar un amistoso, el
    partido nuevo tiene que aparecer en «mis retos» sin recargar la página.
    Cambiar la `key` de un componente lo vuelve a montar, que es la forma más
    corta de decir «pedí todo de nuevo» sin subirle el estado a esta pantalla.
  */
  const [recarga, setRecarga] = useState(0);
  const setVista = (cual: Vista): void => {
    // `replace` para que tocar las cuatro pestañas no deje cuatro pasos en el
    // botón de atrás: cambiar de pestaña no es navegar.
    setParams(cual === 'activity' ? {} : { v: cual }, { replace: true });
  };

  /**
   * LA PESTAÑA PUESTA, A LA VISTA.
   *
   * La barra se corre de lado cuando las cuatro no entran, y entonces la
   * cuarta arranca fuera de cuadro. El aviso de «te retaron» lleva a
   * `/retos?v=mine`: sin esto, la persona llega, ve la barra empezando en
   * «Actividad» y no entiende qué está mirando.
   *
   * `scrollIntoView` no existe en jsdom, así que se pregunta antes: sin la
   * guarda, todas las pruebas de esta pantalla revientan al montarla.
   */
  const barra = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const puesta = barra.current?.querySelector('[aria-selected="true"]');
    if (typeof puesta?.scrollIntoView === 'function') {
      puesta.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [vista]);

  return (
    <AppLayout>
      <h1>{t('challenges.title')}</h1>

      {/* `role="tablist"` de verdad: son tres botones que cambian lo de abajo
          sin cambiar de dirección, y un lector de pantalla tiene que poder
          anunciar cuál está puesta. */}
      <div className="wall-tabs" role="tablist" aria-label={t('wall.title')} ref={barra}>
        {VISTAS.map((cual) => (
          <button
            key={cual}
            type="button"
            role="tab"
            aria-selected={vista === cual}
            className={vista === cual ? 'wall-tab wall-tab-on' : 'wall-tab'}
            onClick={() => setVista(cual)}
          >
            {cual === 'mine' ? t('challenge.mine') : t(`wall.${cual}`)}
          </button>
        ))}
      </div>

      {vista === 'search' ? (
        <>
          <BuscarRival />
          {/* EL CARA A CARA DE DOS CUALESQUIERA, debajo de buscar rival: las
              dos son «encontrar gente», y quien vino a mirar estadísticas de
              otros ya está en la pantalla donde se busca. */}
          <CaraACara />
        </>
      ) : vista === 'mine' ? (
        <>
          {/*
            ANOTAR UN PARTIDO YA JUGADO VA ARRIBA DE «mis retos» y no en una
            pestaña propia: es la misma pregunta —«qué partidos jugué»— y una
            pestaña más para algo que se usa de vez en cuando se convierte en
            una pestaña que nadie encuentra. Arranca plegada.
          */}
          <ReportFriendly onDone={() => setRecarga((n) => n + 1)} />
          <MyChallenges key={recarga} />
        </>
      ) : vista === 'activity' ? (
        <ActivitySection />
      ) : (
        <WallSection />
      )}
    </AppLayout>
  );
}

/**
 * BUSCAR CON QUIÉN JUGAR. Era toda la pantalla de Retos; ahora es su tercera
 * pestaña. El contenido no cambió: los filtros, la lista y el «vs 3» son los
 * mismos.
 */
function BuscarRival(): JSX.Element {
  const { t } = useTranslation();

  const [porRanking, setPorRanking] = useState(false);
  const [porCerca, setPorCerca] = useState(true);
  const [porClub, setPorClub] = useState(false);
  const [sameCategory, setSameCategory] = useState(false);

  const [km, setKm] = useState(CHALLENGE_DEFAULT_KM);
  /** Lo que está escrito AHORA, que puede ir por delante de lo que se buscó. */
  const [texto, setTexto] = useState('');
  const [communityId, setCommunityId] = useState<string>(CUALQUIER_CLUB);

  const [clubes, setClubes] = useState<Community[]>([]);
  const [players, setPlayers] = useState<ChallengePlayer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** El perfil, solo cuando falta la ubicación: es lo que `LocationBox` pide. */
  const [profile, setProfile] = useState<PlayerProfile | null>(null);

  const enCurso = useRef<AbortController | null>(null);

  const kind: ChallengeKind = porRanking ? 'RANKING' : 'FRIENDLY';

  /**
   * Un reto por ranking ES entre gente del mismo club y del mismo ranking. El
   * servidor lo aplica igual —es una regla, no una sugerencia—; acá se
   * muestra prendido y bloqueado para que nadie apague una casilla y crea que
   * sirvió de algo.
   */
  const categoríaPuesta = porRanking || sameCategory;

  /** Con el texto corto, el servidor lo ignora: la pantalla dice lo mismo. */
  const porNombre = texto.trim().length >= CHALLENGE_TEXT_MIN;

  /**
   * LOS KILÓMETROS QUE DE VERDAD VIAJAN, o `null` por no haber límite.
   *
   * Se apaga por dos razones y una no borra a la otra: por destildar la
   * casilla, o porque se está buscando por nombre. `km` se guarda igual en
   * los dos casos — al volver a prender la casilla, o al borrar el texto,
   * vuelve el número que la persona había dejado y no el de fábrica.
   */
  const limiteKm = porNombre || !porCerca ? null : km;

  /**
   * El club que filtra, o `null`.
   *
   * La casilla apagada manda por encima del desplegable: se puede tener un
   * club elegido y la casilla en no, y entonces no filtra. Es lo que permite
   * probar «con y sin club» sin perder cuál era.
   */
  const clubElegido = porClub && communityId !== CUALQUIER_CLUB ? communityId : null;

  /**
   * DE A DIEZ. El servidor manda hasta `CHALLENGE_PAGE_SIZE` —cincuenta—, y
   * cincuenta filas con foto son ocho pantallas de teléfono y cincuenta
   * descargas antes de poder leer la primera.
   *
   * `reiniciarCon` es la búsqueda entera: cada filtro trae OTRA lista, y
   * quedarse en la página 3 de la anterior se lee como que no encontró nada.
   */
  const pagina = usePaginado(
    players ?? [],
    `${kind}|${limiteKm ?? ''}|${texto.trim()}|${clubElegido ?? ''}|${String(categoríaPuesta)}`,
  );

  // La lista de clubes para el desplegable. Una sola vez: no cambia mientras
  // alguien busca, y volver a pedirla en cada tecla sería absurdo.
  useEffect(() => {
    let vivo = true;
    void api
      .listCommunities()
      .then((lista) => {
        if (vivo) setClubes(lista);
      })
      .catch(() => {
        // Que falle la lista NO rompe la pantalla: se busca sin filtrar por
        // club, que es como arranca de todos modos.
      });
    return () => {
      vivo = false;
    };
  }, []);

  const buscar = useCallback(
    async (busqueda: string) => {
      enCurso.current?.abort();
      const control = new AbortController();
      enCurso.current = control;

      setError(null);
      try {
        const encontrados = await api.searchChallengers(
          {
            kind,
            km: limiteKm,
            text: busqueda,
            communityId: clubElegido,
            sameCategory: categoríaPuesta,
          },
          control.signal,
        );
        if (control.signal.aborted) return;
        setPlayers(encontrados);
        setProfile(null);
      } catch (caught) {
        if (control.signal.aborted) return;
        // Falta la ubicación: no es un error que haya que leer, es un botón
        // que hay que ofrecer. Se pide el perfil para poder dibujarlo.
        if (caught instanceof ApiError && caught.code === 'LOCATION_REQUIRED') {
          setPlayers(null);
          try {
            setProfile(await api.myProfile());
          } catch {
            setError(errorMessage(t, caught));
          }
          return;
        }
        /*
         * `null` Y NO `[]`: NO SABEMOS SI HAY GENTE.
         *
         * Acá decía `setPlayers([])`, y una lista vacía en esta pantalla
         * significa «no hay nadie con esos filtros». Con la consulta fallada
         * eso es mentira —no se sabe— y salían los dos mensajes juntos: el
         * error rojo y, debajo, «probá subir los kilómetros». Javier lo vio
         * en producción.
         *
         * Y no es solo feo: manda a la persona a aflojar filtros que no
         * tienen nada que ver, en vez de a reportar que algo se rompió. Un
         * consejo que no puede funcionar gasta el tiempo de quien lo sigue y
         * esconde el problema de quien podría arreglarlo.
         *
         * `null` es el estado que ya significa «todavía no sé», y con el
         * error a la vista la pantalla no dice nada más.
         */
        setPlayers(null);
        setError(errorMessage(t, caught));
      }
    },
    [categoríaPuesta, clubElegido, kind, limiteKm, t],
  );

  /**
   * EL TEXTO ESPERA; LO DEMÁS NO.
   *
   * Escribir «pulguita» son ocho teclas: sin esperar, son ocho consultas que
   * recorren la tabla de perfiles para tirar siete. Mover la barra o cambiar
   * un filtro es un gesto que ya terminó cuando se suelta, así que esos
   * salen de una.
   */
  useEffect(() => {
    const limpio = texto.trim();
    if (limpio === '') {
      void buscar('');
      return;
    }
    const id = setTimeout(() => void buscar(limpio), ESPERA_MS);
    return () => clearTimeout(id);
  }, [buscar, texto]);

  useEffect(() => () => enCurso.current?.abort(), []);


  return (
    <>
      <p className="help">{t('challenges.intro')}</p>

      <div className="field">
        <label htmlFor="challenge-text">{t('challenges.search')}</label>
        <input
          id="challenge-text"
          type="search"
          autoComplete="off"
          placeholder={t('challenges.searchPlaceholder')}
          value={texto}
          onChange={(event) => setTexto(event.target.value)}
        />
      </div>

      {/*
        LOS FILTROS, TODOS EN CASILLAS Y TODOS APAGABLES.

        Un `fieldset` y no cuatro controles sueltos: así se lee de un vistazo
        CUÁLES están puestos, que es la pregunta que uno se hace cuando la
        lista sale vacía. Sueltos por la pantalla, la respuesta obliga a
        recorrerla entera.

        Cada casilla que se prende abre SU control debajo. Tener la barra y el
        desplegable siempre puestos ocupaba media pantalla para decir «no
        estoy filtrando nada».
      */}
      <fieldset className="filtros">
        <legend>{t('challenges.filters')}</legend>

        {/*
          LA DISTANCIA ES OPCIONAL Y SE PUEDE QUITAR. Era el piso de la
          pantalla: sin ubicación no había búsqueda, y a diez kilómetros de
          mucha gente no hay nadie. Destildada se busca en todo el país.

          Buscando por nombre se apaga Y SE DICE POR QUÉ. Dejarla puesta y sin
          efecto sería peor que quitarla: quien la mueve y no ve cambiar nada
          no tiene forma de saber si la pantalla lo ignoró o si de verdad no
          hay nadie.
        */}
        <label className="check">
          <input
            type="checkbox"
            checked={porCerca && !porNombre}
            disabled={porNombre}
            onChange={(event) => setPorCerca(event.target.checked)}
          />
          <span>{t('challenges.nearby')}</span>
        </label>
        {porNombre ? (
          <p className="help filtro-nota">{t('challenges.nationwide')}</p>
        ) : (
          porCerca && (
            /*
              LA DISTANCIA CON UNA BARRA Y NO CON UN CAMPO DE TEXTO. Es un
              número aproximado que se tantea: se arrastra hasta que aparece
              gente. Un campo obliga a pensar un número exacto y a teclearlo,
              para algo que no tiene una respuesta exacta.
            */
            <div className="field distance-field">
              <label htmlFor="challenge-km">{t('challenges.distance', { km })}</label>
              <input
                id="challenge-km"
                type="range"
                min={1}
                max={CHALLENGE_MAX_KM}
                step={1}
                value={km}
                onChange={(event) => setKm(Number(event.target.value))}
              />
            </div>
          )
        )}

        {/*
          AMISTOSO ES NO MARCAR NADA. Era un par de botones «Amistoso / Por
          ranking», y de los dos solo uno cambia algo: el amistoso es con
          cualquiera y sin puntos, o sea la búsqueda sin filtrar. Como casilla
          queda dicho en un renglón en vez de en dos botones.

          La línea de abajo cambia con el estado, y por eso está siempre: una
          explicación que solo aparece cuando se prende deja al que todavía no
          la prendió sin saber qué iba a pasar.
        */}
        <label className="check">
          <input
            type="checkbox"
            checked={porRanking}
            onChange={(event) => setPorRanking(event.target.checked)}
          />
          <span>{t('challenges.ranking')}</span>
        </label>
        <p className="help filtro-nota">
          {porRanking ? t('challenges.rankingHint') : t('challenges.friendlyHint')}
        </p>

        <label className="check filtro-categoria">
          <input
            type="checkbox"
            checked={categoríaPuesta}
            disabled={porRanking}
            onChange={(event) => setSameCategory(event.target.checked)}
          />
          <span>{t('challenges.sameCategory')}</span>
        </label>
        {porRanking && <p className="help filtro-nota">{t('challenges.forcedHint')}</p>}

        <label className="check">
          <input
            type="checkbox"
            checked={porClub}
            onChange={(event) => setPorClub(event.target.checked)}
          />
          <span>{t('challenges.onlyClub')}</span>
        </label>
        {porClub && (
          <div className="field">
            <label htmlFor="challenge-club">{t('challenges.club')}</label>
            <Select
              id="challenge-club"
              value={communityId}
              options={[
                { value: CUALQUIER_CLUB, label: t('challenges.anyClub') },
                ...clubes.map((club) => ({
                  value: club.id,
                  // El pueblo al lado: en Costa Rica hay varios clubes que se
                  // llaman parecido y el nombre solo no alcanza para elegir.
                  ...(club.locationText === '' ? {} : { hint: club.locationText }),
                  label: club.name,
                })),
              ]}
              onChange={setCommunityId}
            />
          </div>
        )}
      </fieldset>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {profile !== null ? (
        <>
          <p className="help">{t('challenges.needLocation')}</p>
          {/*
            LA SEGUNDA PUERTA. Poner la ubicación es un formulario; destildar
            una casilla es un toque. Quien solo quiere ver quién hay tiene que
            enterarse de que puede, y esta pantalla es el único lugar donde
            puede enterarse.
          */}
          <p className="help">{t('challenges.orNationwide')}</p>
          <LocationBox
            profile={profile}
            onChanged={() => {
              setProfile(null);
              void buscar(texto.trim());
            }}
          />
        </>
      ) : players === null ? (
        /*
          CON UN ERROR A LA VISTA, NADA MÁS.
          `null` es «todavía no sé», y eso puede ser porque está cargando o
          porque la consulta falló. Con el error ya dibujado arriba, decir
          además «Cargando…» sería prometer una lista que no va a llegar.
        */
        error !== null ? null : <p className="help">{t('common.loading')}</p>
      ) : players.length === 0 ? (
        /*
          TRES VACÍOS DISTINTOS Y TRES FRASES DISTINTAS. «No hay nadie» a secas
          no dice qué hacer, y lo que hay que hacer cambia: buscando por
          nombre no hay filtro que aflojar, con la distancia puesta lo primero
          es subirla o quitarla, y sin distancia lo único que queda es soltar
          alguna de las otras casillas. La frase de subir los kilómetros
          cuando la distancia está apagada era un consejo imposible de seguir.
        */
        <p className="help">
          {porNombre
            ? t('challenges.noName')
            : limiteKm === null
              ? t('challenges.emptyWide')
              : t('challenges.empty')}
        </p>
      ) : (
        <>
        <div className="table-scroll">
        <table className="people-table challenge-list">
          <thead>
            {/*
              DOS COLUMNAS Y NO TRES. El botón de contactar era una tercera
              columna, y con tres columnas la tabla no entra en un teléfono:
              había que arrastrarla de lado para llegar al botón que uno vino
              a apretar. Ahora vive DEBAJO del nombre, en la misma celda, y la
              tabla entra sin arrastrar nada.
            */}
            <tr>
              <th scope="col">{t('player.player')}</th>
              <th scope="col">{t('ranking.category')}</th>
            </tr>
          </thead>
          <tbody>
          {pagina.visibles.map((player) => (
            <tr key={player.userId}>
              {/*
                EL NOMBRE COMPLETO SE MUESTRA ACÁ Y EN NINGUNA OTRA PANTALLA.
                Quien aparece en esta lista prendió «quiero recibir retos»,
                que es justamente el permiso para que alguien lo reconozca y
                lo llame. Sin eso no está en la lista.
              */}
              <PersonCell
                userId={player.userId}
                displayName={player.displayName}
                avatarPath={player.avatarPath}
                note={player.fullName}
                debajo={<VecesJugadas userId={player.userId} veces={player.playedTogether} />}
              />
              <td>
                <span className="help">
                  {[
                    // EL NIVEL DEL CLUB Y EL DECLARADO NO SE MEZCLAN. El del
                    // club viene respaldado por alguien que lo fijó; el
                    // declarado lo dijo la persona de sí misma, y se muestra
                    // con esa palabra al lado para que se sepa cuál se está
                    // leyendo. Cuando hay club, manda el club.
                    //
                    // «Cat A» Y NO «A» A SECAS: al lado de «Cartago,
                    // Turrialba · a 1.8 km», una letra suelta no se lee como
                    // una categoría — se lee como una inicial o como basura.
                    player.level !== null
                      ? t('challenges.categoryTag', {
                          level: t(`categoryLevel.${player.level}`),
                        })
                      : player.declaredLevel === null
                        ? null
                        : t('challenges.declaredLevel', {
                            level: t(`categoryLevel.${player.declaredLevel}`),
                          }),
                    player.communityName,
                    player.locationLabel,
                    player.distanceKm === null
                      ? null
                      : t('challenges.away', { km: player.distanceKm }),
                  ]
                    .filter((parte) => parte !== null && parte !== '')
                    .join(' · ')}
                </span>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
        </div>
        <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />
        </>
      )}
    </>
  );
}

/**
 * «VS 3»: CUÁNTAS VECES YA JUGAMOS CONTRA ESTA PERSONA.
 *
 * REEMPLAZÓ AL BOTÓN DE WHATSAPP, y el cambio es más grande de lo que
 * parece. El botón hacía que la lista fuera un directorio telefónico: lo
 * único que se podía hacer con una fila era escribirle a un desconocido. El
 * teléfono sigue estando, pero en el perfil —donde además están su club, su
 * categoría y sus partidos—, así que antes de escribirle uno ve con quién
 * está hablando. La fila quedó para lo que sirve: reconocer a alguien.
 *
 * Y ESTE NÚMERO ES LO QUE HACE QUE SE RECONOZCA. En una lista de treinta
 * nombres, «contra este ya jugué tres veces» es el único dato que separa a un
 * conocido de un desconocido.
 *
 * SIN PARTIDOS NO SE DIBUJA NADA. Un «vs 0» al lado de alguien que uno nunca
 * vio no dice nada, y ocupa el renglón que necesita el que sí tiene número.
 */
function VecesJugadas({ userId, veces }: { userId: string; veces: number }): JSX.Element | null {
  const { t } = useTranslation();
  if (veces <= 0) return null;

  /**
   * Y SE PUEDE APRETAR: el número invita a la pregunta —«¿cómo me fue?»— y
   * tenerla al lado sin poder abrirla es peor que no tenerla. Lleva al cara a
   * cara, que es donde está la respuesta entera.
   */
  return (
    <Link className="tag tag-vs" to={`/jugadores/${userId}/vs`}>
      {t('challenges.playedTogether', { count: veces })}
    </Link>
  );
}
